import { useMemo } from 'react';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { formatUTCInUserTimeZone } from 'src/utilities/DateTimeUtilities';
import type { SnapshotTagRow } from 'src/components/star-grid/starGridTypes';
import { snapshotDisplayName } from 'src/components/star-grid/utils/snapshotTagLabel';

type Getter = ValueGetterParams<SnapshotTagRow>;

/** The bordered grid theme the admin lists share. */
export const useAdminGridTheme = () => {
  const { agGridTheme } = useTheme();
  return useMemo(
    () =>
      agGridTheme.withParams({
        fontSize: 12,
        headerFontSize: 11,
        cellHorizontalPadding: 10,
        borderColor: '#d5dbdb',
        wrapperBorder: true,
        rowBorder: true,
        columnBorder: true,
        headerColumnBorder: true,
        headerRowBorder: true,
        wrapperBorderRadius: 8,
        headerBackgroundColor: '#f2f3f3',
        oddRowBackgroundColor: '#ffffff'
      }),
    [agGridTheme]
  );
};

/** Columns for the Snapshot Manager: Tag, Short Description, Snapshot Timestamp, Created By, Age. */
export const useSnapshotGrid = (actions: { readonly cellRenderer: string }) => {
  const theme = useAdminGridTheme();
  const columns = useMemo<ColDef<SnapshotTagRow>[]>(
    () => [
      {
        colId: 'tag',
        headerName: 'Tag',
        minWidth: 200,
        flex: 2,
        valueGetter: ({ data }: Getter) => (data ? snapshotDisplayName(data) : ''),
        tooltipValueGetter: ({ data }) => data?.tag ?? '',
        cellClass: 'sg-snapshot-version'
      },
      { colId: 'description', headerName: 'Short Description', minWidth: 220, flex: 2, valueGetter: ({ data }: Getter) => data?.description ?? '' },
      {
        colId: 'committed_at',
        headerName: 'Snapshot Timestamp',
        minWidth: 200,
        flex: 1,
        valueGetter: ({ data }: Getter) => (data?.committed_at ? formatUTCInUserTimeZone(data.committed_at) : '')
      },
      { colId: 'created_by', headerName: 'Created By', width: 140, valueGetter: ({ data }: Getter) => data?.created_by ?? '' },
      { colId: 'age', headerName: 'Age (Days)', width: 120, type: 'numericColumn', valueGetter: ({ data }: Getter) => data?.age_days ?? null },
      { colId: 'snapshot_id', headerName: 'Snapshot ID', minWidth: 190, flex: 1, valueGetter: ({ data }: Getter) => data?.snapshot_id ?? '' },
      {
        colId: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: actions.cellRenderer,
        cellClass: 'sg-snapshot-actions'
      }
    ],
    [actions.cellRenderer]
  );
  return { theme, columns };
};

export type SnapshotActionParams = ICellRendererParams<SnapshotTagRow>;
